<template>
	<div class="textbookReport" v-loading="loading">
		<header class="reportChrome">
			<div class="reportNavigation">
				<button class="backButton" type="button" @click="$emit('fhBtn', 'keben')">
					<i class="el-icon-arrow-left"></i>返回课本
				</button>
				<div class="scopeTabs" aria-label="课本统计页面切换">
					<button type="button" :class="{ active: scopeMode === 'all' }" @click="switchScope('all')">整书概览</button>
					<button type="button" :class="{ active: scopeMode === 'chapter' }" @click="switchScope('chapter')">章节报告</button>
				</div>
			</div>

			<div v-if="scopeMode === 'all'" class="bookTitleRow">
				<h2 :title="context.textbookTitle || '班级课本统计'">{{ context.textbookTitle || '班级课本统计' }}</h2>
			</div>
			<div v-else class="chapterToolbar">
				<span class="toolbarLabel">指定章节</span>
				<el-select
					v-model="selectedDirectoryId"
					filterable
					placeholder="请选择章节"
					@change="chapterSelectionChanged">
					<el-option
						v-for="item in chapters"
						:key="item.textbookDirectoryId"
						:label="item.displayName"
						:value="Number(item.textbookDirectoryId)">
					</el-option>
				</el-select>
				<el-button type="primary" icon="el-icon-download" @click="openChapterExport">导出报告</el-button>
			</div>
		</header>

		<daily-answer-report
			v-if="scopeMode === 'chapter' && selectedChapter"
			:key="`chapter-${selectedDirectoryId}`"
			ref="chapterReport"
			:bj-id="bjId"
			report-source="textbookChapter"
			:textbook-id="ztId"
			:user-textbook-id="userTextbookId"
			:chapter-report-item="selectedChapter"
			embedded
			@chapter-back="showWholeBook"
		></daily-answer-report>
		<div v-else-if="scopeMode === 'chapter'" class="emptyChapterReport">
			<el-empty description="当前章节信息无效，请返回整本统计后重新选择">
				<el-button type="primary" @click="showWholeBook">返回整本统计</el-button>
			</el-empty>
		</div>
		<template v-else>
		<template v-if="!loading || chapters.length">
			<div class="overviewGrid">
				<div v-for="item in overviewCards" :key="item.label" class="overviewCard" :style="{ '--card-color': item.color }">
					<span>{{ item.label }}</span>
					<b>{{ item.value }}</b>
					<small>{{ item.note }}</small>
				</div>
			</div>

			<section class="reportPanel chapterPanel">
				<div class="panelTitle">
					<div>
						<h3>{{ scopeMode === 'all' ? '章节完成情况' : '章节统计' }}</h3>
						<p>{{ scopeMode === 'all' ? '预览全书所有章节，点击章节名称可直接进入对应章节统计' : '当前章节的班级答题汇总' }}</p>
					</div>
				</div>
				<el-table
					v-if="chapterRows.length"
					:data="chapterRows"
					border
					class="chapterTable"
					row-key="textbookDirectoryId"
					:row-class-name="chapterRowClass"
					@row-click="openChapter"
				>
					<el-table-column label="章节" min-width="230" fixed>
						<template slot-scope="scope">
							<el-button type="text" class="chapterLink" @click.stop="openChapter(scope.row)">
								{{ scope.row.displayName }}<i class="el-icon-arrow-right"></i>
							</el-button>
						</template>
					</el-table-column>
					<el-table-column prop="questionCount" label="题目数量" width="92" align="center"></el-table-column>
					<el-table-column prop="submittedCount" label="答题人数" width="92" align="center"></el-table-column>
					<el-table-column label="正确率" width="100" align="center">
						<template slot-scope="scope"><b :style="{ color: rateColor(scope.row.rightRate) }">{{ formatRate(scope.row.rightRate) }}%</b></template>
					</el-table-column>
					<el-table-column label="平均分" width="95" align="center">
						<template slot-scope="scope">{{ formatScore(scope.row.averageScore) }}</template>
					</el-table-column>
					<el-table-column prop="answerCount" label="答题总数" width="92" align="center"></el-table-column>
					<el-table-column prop="rightCount" label="正确总数" width="92" align="center"></el-table-column>
					<el-table-column prop="wrongCount" label="错误总数" width="92" align="center"></el-table-column>
					<el-table-column prop="blankCount" label="空题总数" width="92" align="center"></el-table-column>
				</el-table>
				<el-empty v-else :image-size="70" description="当前范围暂无章节统计数据"></el-empty>
			</section>
		</template>
		</template>
	</div>
</template>

<script>
import dailyAnswerReport from './dailyAnswerReport';

export default {
	name: 'textbookReport',
	props: ['bjId', 'ztId', 'userTextbookId'],
	components: { dailyAnswerReport },
	data() {
		return {
			loading: false,
			scopeMode: 'all',
			selectedDirectoryId: '',
			chapters: [],
			context: {},
			summary: {},
			chapterRows: []
		};
	},
	computed: {
		selectedChapter() {
			return this.chapters.find(item => Number(item.textbookDirectoryId) === Number(this.selectedDirectoryId)) || null;
		},
		overviewCards() {
			const s = this.summary || {};
			const dimension = this.scopeMode === 'all' ? '全书' : '当前章节';
			return [
				{ label: '已完成', value: this.formatCount(s.completedCount), note: `${dimension}正确与错误题目总数`, color: '#3377e6' },
				{ label: '未完成', value: this.formatCount(s.unfinishedCount), note: `${dimension}空题及尚未作答题目`, color: '#8b95a5' },
				{ label: '正确', value: this.formatCount(s.rightCount), note: '首次正式答题中的正确总数', color: '#20a878' },
				{ label: '错误', value: this.formatCount(s.wrongCount), note: '首次正式答题中的错误总数', color: '#f07b45' },
				{ label: '空题', value: this.formatCount(s.blankCount), note: '提交后未作答的题目总数', color: '#e0a52b' },
				{ label: '完成率', value: `${this.formatRate(s.completionRate)}%`, note: `已完成 ÷ ${dimension}所有题`, color: '#45a3ff' },
				{ label: '得分率', value: `${this.formatRate(s.scoreRate)}%`, note: '正确 ÷ 已完成', color: '#7656d6' },
				{ label: this.scopeMode === 'all' ? '书本难度' : '章节难度', value: s.difficulty || '困难', note: '按得分率自动评级', color: this.difficultyColor(s.difficulty) }
			];
		}
	},
	methods: {
		requestParams() {
			return {
				textbookId: this.ztId,
				userTextbookId: this.userTextbookId,
				bjId: this.bjId,
				directoryIds: this.scopeMode === 'chapter' && this.selectedDirectoryId ? String(this.selectedDirectoryId) : ''
			};
		},
		async loadDashboard() {
			if (!this.ztId || !this.userTextbookId || !this.bjId) {
				this.$message.warning('缺少班级课本信息');
				return;
			}
			if (this.scopeMode === 'chapter' && !this.selectedDirectoryId) return;
			this.loading = true;
			try {
				const res = await this.$http.get('api/app/yydTextbookReport/dashboard', { params: this.requestParams() });
				if (res.code === 200 || res.code === 0) {
					const data = res.data || {};
					this.context = data.context || {};
					this.chapters = data.chapters || [];
					this.summary = data.summary || {};
					this.chapterRows = data.chapterRows || [];
				}
			} catch (error) {
				const message = error && error.response && error.response.data && error.response.data.msg;
				this.$message.error(message || '课本统计加载失败，请稍后重试');
			} finally {
				this.loading = false;
			}
		},
		openChapter(row) {
			if (!row || !row.textbookDirectoryId) return;
			this.selectedDirectoryId = Number(row.textbookDirectoryId);
			this.scopeMode = 'chapter';
			this.$nextTick(() => this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
		},
		switchScope(mode) {
			if (mode === 'all') {
				this.showWholeBook();
				return;
			}
			if (!this.chapters.length) {
				this.$message.warning('当前课本暂无可统计的章节');
				return;
			}
			if (!this.selectedChapter) this.selectedDirectoryId = Number(this.chapters[0].textbookDirectoryId);
			this.scopeMode = 'chapter';
		},
		chapterSelectionChanged(value) {
			this.selectedDirectoryId = Number(value);
			this.scopeMode = 'chapter';
		},
		openChapterExport() {
			const report = this.$refs.chapterReport;
			if (!report || !report.hasReport) {
				this.$message.warning('章节答题报告尚未加载完成');
				return;
			}
			report.openExportDialog();
		},
		chapterRowClass({ row }) {
			return Number(row.textbookDirectoryId) === Number(this.selectedDirectoryId)
				? 'chapterRow selectedChapterRow'
				: 'chapterRow';
		},
		showWholeBook() {
			this.scopeMode = 'all';
			if (!this.chapterRows.length) this.loadDashboard();
		},
		formatCount(value) { return Number(value || 0).toLocaleString('zh-CN'); },
		formatRate(value) { return Number(value || 0).toFixed(2); },
		formatScore(value) { return `${Number(value || 0).toFixed(2)}分`; },
		rateColor(value) {
			const rate = Number(value || 0);
			if (rate < 60) return '#f56c6c';
			if (rate < 80) return '#e6a23c';
			return '#20a878';
		},
		difficultyColor(value) {
			if (value === '简单') return '#20a878';
			if (value === '良好') return '#45a3ff';
			if (value === '中等') return '#e0a52b';
			return '#f56c6c';
		}
	},
	mounted() { this.loadDashboard(); }
};
</script>

<style lang="scss" scoped>
.textbookReport { width: 100%; min-width: 0; min-height: 650px; padding-bottom: 24px; box-sizing: border-box; color: #26313d; }
.reportChrome { background: #fff; border-bottom: 1px solid #e5e9ef; }
.reportNavigation { min-height: 76px; padding: 13px 18px; display: flex; align-items: center; justify-content: space-between; gap: 18px; box-sizing: border-box; }
.backButton { padding: 0; border: 0; background: transparent; cursor: pointer; white-space: nowrap; font-size: 14px; color: #333; i { margin-right: 4px; } }
.scopeTabs { display: inline-flex; border: 1px solid #cfd5dc; border-radius: 4px; overflow: hidden; background: #fff; button { min-width: 92px; height: 38px; padding: 0 18px; border: 0; border-right: 1px solid #cfd5dc; background: #fff; color: #35404c; cursor: pointer; font-size: 14px; &:last-child { border-right: 0; } &.active { background: #29323c; color: #fff; } } }
.bookTitleRow { min-height: 68px; padding: 0 18px 0 32px; border-top: 1px solid #edf0f4; display: flex; align-items: center; box-sizing: border-box; h2 { min-width: 0; margin: 0; overflow: hidden; color: #27313d; font-size: 18px; font-weight: 500; text-align: left; text-overflow: ellipsis; white-space: nowrap; } }
.chapterToolbar { min-height: 76px; padding: 12px 18px 12px 32px; border-top: 1px solid #edf0f4; display: grid; grid-template-columns: auto minmax(280px, 1fr) auto; align-items: center; gap: 20px; box-sizing: border-box; .toolbarLabel { color: #3e4854; font-size: 14px; text-align: left; } .el-select { width: 100%; } }
.emptyChapterReport { min-height: 560px; padding-top: 80px; box-sizing: border-box; background: #fff; }
.overviewGrid { margin: 16px; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.overviewCard { min-height: 105px; padding: 15px 16px; border: 1px solid #e8edf3; border-top: 3px solid var(--card-color); border-radius: 8px; background: #fff; box-shadow: 0 3px 12px rgba(32, 52, 76, .05); span { color: #77808d; font-size: 13px; } b { display: block; margin: 8px 0 5px; color: var(--card-color); font-size: 24px; line-height: 1; } small { color: #a0a7b1; font-size: 11px; } }
.reportPanel { margin: 0 16px 16px; padding: 17px; border: 1px solid #e7ebf0; border-radius: 9px; background: #fff; box-shadow: 0 3px 12px rgba(32, 52, 76, .04); }
.panelTitle { margin-bottom: 16px; h3 { margin: 0 0 5px; font-size: 16px; } p { margin: 0; color: #929aa5; font-size: 12px; } }
.chapterTable { width: 100%; }
.chapterLink {
	max-width: 100%;
	font-weight: 600;
	text-align: left;
	white-space: normal;
	line-height: 20px;
	i { margin-left: 5px; }
	&:hover, &:focus {
		border-color: transparent !important;
		outline: none;
		background-color: transparent !important;
		box-shadow: none;
	}
}
::v-deep .chapterRow { cursor: pointer; }
::v-deep .chapterRow:hover > td { background: #f1f7ff !important; }
::v-deep .selectedChapterRow > td { background: #eef0f3 !important; }
@media (max-width: 1100px) {
	.overviewGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
	.bookTitleRow, .chapterToolbar { padding-left: 32px; padding-right: 18px; }
	.chapterToolbar { grid-template-columns: auto minmax(220px, 1fr) auto; gap: 12px; }
}
</style>
